import { useState, useEffect, useMemo } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { supabase, Category } from "@/lib/supabase";

const emptyForm = { name: "", description: "", image: "" };

const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editCat, setEditCat] = useState<Category | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [search, setSearch] = useState("");

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchCategories = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      showToast("Failed to load categories.", "error");
    } else {
      setCategories(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.toLowerCase();
    return categories.filter(
      (c) => c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
    );
  }, [categories, search]);

  const openAdd = () => {
    setEditCat(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (cat: Category) => {
    setEditCat(cat);
    setForm({ name: cat.name, description: cat.description, image: cat.image });
    setModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    if (editCat) {
      const { error } = await supabase
        .from("categories")
        .update({ name: form.name, description: form.description, image: form.image })
        .eq("id", editCat.id);
      if (error) {
        showToast("Failed to update category.", "error");
      } else {
        showToast("Category updated successfully!");
        await fetchCategories();
      }
    } else {
      const slug =
        form.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") +
        "-" +
        Date.now();
      const { error } = await supabase.from("categories").insert({
        id: slug,
        name: form.name,
        description: form.description,
        image: form.image,
        count: 0,
      });
      if (error) {
        showToast("Failed to add category.", "error");
      } else {
        showToast("Category added successfully!");
        await fetchCategories();
      }
    }
    setSaving(false);
    setModalOpen(false);
    setEditCat(null);
    setForm(emptyForm);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error } = await supabase.from("categories").delete().eq("id", deleteTarget.id);
    if (error) {
      showToast("Failed to delete category.", "error");
    } else {
      showToast("Category deleted.", "error");
      await fetchCategories();
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {toast && (
          <div
            className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl text-white text-sm font-medium ${
              toast.type === "success"
                ? "bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]"
                : "bg-red-500"
            }`}
          >
            <i className={toast.type === "success" ? "ri-check-line text-lg" : "ri-delete-bin-line text-lg"} />
            {toast.msg}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1
              className="text-2xl font-bold text-gray-900"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Categories
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">{categories.length} total categories</p>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white text-sm font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Add Category
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-5">
          <div className="relative max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] text-sm text-gray-700 placeholder-gray-400 outline-none transition-all"
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-10 h-10 border-4 border-[#3a8cbf]/20 border-t-[#3a8cbf] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
              <i className="ri-folder-line text-2xl text-gray-300" />
            </div>
            <p className="text-gray-500 font-medium">No categories found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((cat) => (
              <div
                key={cat.id}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:-translate-y-0.5 transition-all duration-200 group"
              >
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-3 left-3">
                    <span className="text-xs font-medium text-white/90 bg-black/30 px-2 py-0.5 rounded-full">
                      {cat.count} products
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3
                      className="font-bold text-gray-900 text-base"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {cat.name}
                    </h3>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {cat.created_at?.split("T")[0]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">{cat.description}</p>
                  <div className="flex items-center gap-1 mb-4">
                    <i className="ri-price-tag-3-line text-xs text-gray-400" />
                    <code className="text-xs text-gray-400 font-mono truncate">{cat.id}</code>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEdit(cat)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gray-50 hover:bg-[#3a8cbf]/10 text-gray-600 hover:text-[#3a8cbf] text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-edit-line text-sm" />
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteTarget(cat)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gray-50 hover:bg-red-50 text-gray-600 hover:text-red-500 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-6-line text-sm" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setModalOpen(false)}
          />
          <div className="relative bg-white rounded-2xl w-full max-w-lg z-10">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2
                className="text-lg font-bold text-gray-900"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {editCat ? "Edit Category" : "Add New Category"}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-gray-500 text-xl" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Category Name *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Home Decor"
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Description *
                </label>
                <input
                  type="text"
                  required
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Short tagline for this category"
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Image URL *
                </label>
                <input
                  type="url"
                  required
                  value={form.image}
                  onChange={(e) => setForm((p) => ({ ...p, image: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                />
                {form.image && (
                  <img
                    src={form.image}
                    alt="preview"
                    className="mt-2 w-20 h-20 rounded-xl object-cover border border-gray-100"
                  />
                )}
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white text-sm font-semibold hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {saving ? "Saving..." : editCat ? "Save Changes" : "Add Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDeleteTarget(null)}
          />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-6 z-10">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-red-50 mx-auto mb-4">
              <i className="ri-delete-bin-6-line text-red-500 text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Delete Category?</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-700">&quot;{deleteTarget.name}&quot;</span>?
              This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCategoriesPage;
